import { lazy, Suspense, useEffect } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { WEB_ROUTES } from '@wonderherbals/shared';
import ErrorBoundary from '@/components/ErrorBoundary';
import ProtectedRoute from '@/components/ProtectedRoute';
import { PageLoader } from '@/components/LoadingSpinner';

// Layout — eager import since every route needs it
import Layout from '@/components/Layout';

// Pages — lazy loaded for code splitting
const HomePage = lazy(() => import('@/pages/Home'));
const ShopPage = lazy(() => import('@/pages/Shop'));
const ProductPage = lazy(() => import('@/pages/ProductPage'));
const CollectionPage = lazy(() => import('@/pages/CollectionPage'));
const CategoryPage = lazy(() => import('@/pages/Category'));
const IngredientPage = lazy(() => import('@/pages/IngredientPage'));
const IngredientsListPage = lazy(() => import('@/pages/IngredientsList'));
const ConcernPage = lazy(() => import('@/pages/ConcernPage'));
const ConcernsListPage = lazy(() => import('@/pages/ConcernsList'));
const ContactPage = lazy(() => import('@/pages/Contact'));
const BlogPage = lazy(() => import('@/pages/Blog'));
const BlogPostPage = lazy(() => import('@/pages/BlogPost'));
const AyurvedaPage = lazy(() => import('@/pages/Ayurveda'));
const DoshaQuizPage = lazy(() => import('@/pages/DoshaQuiz'));
const RitucharyaPage = lazy(() => import('@/pages/Ritucharya'));
const CartPage = lazy(() => import('@/pages/Cart'));
const CheckoutPage = lazy(() => import('@/pages/CheckoutPage'));
const AuthPage = lazy(() => import('@/pages/AuthPage'));
const AuthCallback = lazy(() => import('@/pages/AuthCallback'));
const AccountPage = lazy(() => import('@/pages/Account'));
const OrderSuccessPage = lazy(() => import('@/pages/OrderSuccess'));
const TrackOrderPage = lazy(() => import('@/pages/TrackOrder'));
const SearchPage = lazy(() => import('@/pages/Search'));
const NotFoundPage = lazy(() => import('@/pages/NotFound'));

/**
 * Scroll to top on route change — essential for SPA navigation
 */
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [pathname]);

  return null;
}

export default function App() {
  const location = useLocation();

  return (
    <ErrorBoundary>
      <ScrollToTop />
      <Suspense fallback={<PageLoader />}>
        <AnimatePresence mode="wait">
          <Routes location={location} key={location.pathname}>
            <Route element={<Layout />}>
              {/* ── Public Pages ─────────────────────────── */}
              <Route path={WEB_ROUTES.HOME} element={<HomePage />} />
              <Route path={WEB_ROUTES.SHOP} element={<ShopPage />} />
              <Route path={WEB_ROUTES.PRODUCT} element={<ProductPage />} />
              <Route path={WEB_ROUTES.COLLECTION} element={<CollectionPage />} />
              <Route path="/collections/:slug" element={<CollectionPage />} />
              <Route path="/collections" element={<CollectionPage />} />
              <Route path={WEB_ROUTES.CATEGORY} element={<CategoryPage />} />
              <Route path="/categories/:slug" element={<CategoryPage />} />
              <Route path={WEB_ROUTES.INGREDIENT} element={<IngredientPage />} />
              <Route path={WEB_ROUTES.INGREDIENTS} element={<IngredientsListPage />} />
              <Route path={WEB_ROUTES.CONCERN} element={<ConcernPage />} />
              <Route path={WEB_ROUTES.CONCERNS} element={<ConcernsListPage />} />
              <Route path={WEB_ROUTES.CONTACT} element={<ContactPage />} />

              {/* ── Blog ─────────────────────────────────── */}
              <Route path={WEB_ROUTES.BLOG} element={<BlogPage />} />
              <Route path={WEB_ROUTES.BLOG_POST} element={<BlogPostPage />} />

              {/* ── Ayurveda ─────────────────────────────── */}
              <Route path={WEB_ROUTES.AYURVEDA} element={<AyurvedaPage />} />
              <Route path={WEB_ROUTES.DOSHA_QUIZ} element={<DoshaQuizPage />} />
              <Route path="/prakriti" element={<DoshaQuizPage />} />
              <Route path="/dosha-quiz" element={<DoshaQuizPage />} />
              <Route path="/ritucharya" element={<RitucharyaPage />} />

              {/* ── Cart ──────────────────────────────────── */}
              <Route path={WEB_ROUTES.CART} element={<CartPage />} />

              {/* ── Search ────────────────────────────────── */}
              <Route path={WEB_ROUTES.SEARCH} element={<SearchPage />} />

              {/* ── Auth ──────────────────────────────────── */}
              <Route path={WEB_ROUTES.LOGIN} element={<AuthPage />} />
              <Route path={WEB_ROUTES.REGISTER} element={<AuthPage />} />
              <Route path="/auth" element={<AuthPage />} />
              <Route path="/auth/callback" element={<AuthCallback />} />

              {/* ── Checkout ──────────────────────────────── */}
              <Route path={WEB_ROUTES.CHECKOUT} element={<CheckoutPage />} />
              <Route path={WEB_ROUTES.ORDER_CONFIRMATION} element={<OrderSuccessPage />} />
              <Route path="/order/confirmation/:orderNumber" element={<OrderSuccessPage />} />
              <Route path="/order/success/:orderNumber" element={<OrderSuccessPage />} />
              <Route path="/order/success" element={<OrderSuccessPage />} />
              <Route path={WEB_ROUTES.TRACK} element={<TrackOrderPage />} />
              <Route path="/track/:id" element={<TrackOrderPage />} />

              {/* ── Protected Routes ──────────────────────── */}
              <Route
                path={WEB_ROUTES.ACCOUNT}
                element={
                  <ProtectedRoute>
                    <AccountPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path={WEB_ROUTES.ACCOUNT_ORDERS}
                element={
                  <ProtectedRoute>
                    <AccountPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path={WEB_ROUTES.ACCOUNT_ORDER}
                element={
                  <ProtectedRoute>
                    <AccountPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path={WEB_ROUTES.ACCOUNT_ADDRESSES}
                element={
                  <ProtectedRoute>
                    <AccountPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path={WEB_ROUTES.ACCOUNT_PROFILE}
                element={
                  <ProtectedRoute>
                    <AccountPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path={WEB_ROUTES.ACCOUNT_WISHLIST}
                element={
                  <ProtectedRoute>
                    <AccountPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path={WEB_ROUTES.ACCOUNT_LOYALTY}
                element={
                  <ProtectedRoute>
                    <AccountPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path={WEB_ROUTES.ACCOUNT_DOSHA}
                element={
                  <ProtectedRoute>
                    <AccountPage />
                  </ProtectedRoute>
                }
              />

              {/* ── 404 ───────────────────────────────────── */}
              <Route path="*" element={<NotFoundPage />} />
            </Route>
          </Routes>
        </AnimatePresence>
      </Suspense>
    </ErrorBoundary>
  );
}
